import { Button, Divider, Group, NumberInput, Stack, Switch, Text, TextInput } from "@mantine/core";
import { IGisPoint, IValueLabel } from "@mctypes/global.types";
import { IMaconomyCoordinates } from "@mctypes/report.types";
import proj4 from "proj4";
import React from "react";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { IGeoteknikKoordinater } from "@mcapps/shared/reportSchemas/geoteknikSchema";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";
import { useUploadFileToAzureBlobStorageMutation } from "../../../azureBlobStorageRTK";
import { dataURLToFile } from "../../admin/selectors";
import { labelWeight } from "../reportBoardConstants";
import { fetchReportAndUpdateUploadedFilesThunk } from "../reportSlice";
import "./GeoteknikCSS.css";
import SingleGeoteknikReportGisMap from "./SingleGeoteknikReportGisMap";
import { selectHttpSingleReportBoard } from "../reportSelectors";

const GeoteknikMaps: React.FC<{
  geoteknikKoordinater: IGeoteknikKoordinater;
  onUpdateGeoteknikKoordinater: (koordinater: IGeoteknikKoordinater) => void;
  sirkelDiameter: number;
  onUpdateSirkelDiameter: (key: string, value: number) => void;
  zoomLevel: number;
  onUpdateZoomLevel: (zoom: number) => void;
}> = ({
  geoteknikKoordinater,
  onUpdateGeoteknikKoordinater,
  sirkelDiameter,
  onUpdateSirkelDiameter,
  zoomLevel,
  onUpdateZoomLevel,
}) => {
  const [gisPoint, setGisPoint] = React.useState<IGisPoint>(null);
  const [pinVisible, setPinVisible] = React.useState<boolean>(false);
  const [circleVisible, setCircleVisible] = React.useState<boolean>(true);

  const [activeMap, setActiveMap] = React.useState<string>("kart1");

  const maps: IValueLabel[] = [
    { value: "kart1", label: "Gråtone" },
    { value: "kart2", label: "Flyfoto" },
    { value: "kart3", label: "NGU løsmassekart" },
    { value: "kart4", label: "NVE - Fare - Kvikkleiresoner" },
  ];

  const project = useAppSelector((state) => state.adminReducer.project);
  const activeReport = useAppSelector(selectHttpSingleReportBoard); //state.reportReducer.activeReport;
  const [gisMapWasLoaded, setGisMapWasLoaded] = React.useState<boolean>(false);
  const [uploadFile, { isLoading: fileIsUploading }] = useUploadFileToAzureBlobStorageMutation();

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    logicToHandlePinAndMoveTarget();
  }, [geoteknikKoordinater, gisMapWasLoaded]);

  const logicToHandlePinAndMoveTarget = () => {
    if (geoteknikKoordinater != null && gisMapWasLoaded === true) {
      const _gisPoint = convertGeoteknikKoordinaterToGisPoints(geoteknikKoordinater);
      setGisPoint(_gisPoint);
    } else {
      setGisPoint(null);
    }
  };

  const storeScreenShot = async (image: string, docxImageTag: string) => {
    const file = dataURLToFile(image, `${docxImageTag}.jpg`);
    const path = `${project._id}/report/${activeReport._id}/images/${docxImageTag}`;
    await uploadFile({ projectId: project?._id, file, path });

    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.FILE_UPLOADED));
    dispatch(fetchReportAndUpdateUploadedFilesThunk(activeReport._id));
  };

  const logicWhenMapIsLoaded = () => {
    setGisMapWasLoaded(true);
  };

  return (
    <Stack gap="md">
      <Divider
        label={
          <Text fz="lg" fw={600}>
            Kart
          </Text>
        }
        labelPosition="center"
        mb="sm"
      />

      {/* GisPoint */}
      <GisPoint
        geoteknikKoordinater={geoteknikKoordinater}
        onUpdateGeoteknikKoordinater={onUpdateGeoteknikKoordinater}
      />

      {/* Map buttons */}
      {maps && (
        <Group gap="xs">
          {maps.map((map) => (
            <Button
              key={map.value}
              onClick={() => setActiveMap(map.value)}
              size="sm"
              variant={map.value === activeMap ? "primary" : "outline"}
            >
              {map.label}
            </Button>
          ))}
        </Group>
      )}

      {/* Pin/Circle controls */}
      {gisPoint && (
        <Stack gap="sm" style={{ border: "1px solid #ddd", padding: 12, width: 300 }}>
          <Switch
            label="Vis pin"
            checked={pinVisible}
            onChange={() => setPinVisible(!pinVisible)}
            color="blue"
            size="sm"
          />
          <Switch
            label="Vis sirkel"
            checked={circleVisible}
            onChange={() => setCircleVisible(!circleVisible)}
            color="blue"
            size="sm"
          />
          <Stack gap={4}>
            <Text fw={500}>Diameter sirkel (m)</Text>
            <TextInput
              size="sm"
              value={sirkelDiameter}
              onChange={(e) => onUpdateSirkelDiameter("prosjektInformasjon.sirkelDiameter", Number(e.target.value))}
            />
          </Stack>
        </Stack>
      )}

      {/* Maps */}
      {activeMap === "kart1" && (
        <SingleGeoteknikReportGisMap
          pinVisible={pinVisible ? "YES" : "NO"}
          circleVisible={circleVisible ? "YES" : "NO"}
          sirkelDiameter={sirkelDiameter}
          mapId="39bf7f38ae82498b8427a3be4cb3a4e1"
          groupId="43b82823b8d34a438add6c6e88a79b0c"
          pin={gisPoint}
          storeScreenShot={storeScreenShot}
          onLoaded={logicWhenMapIsLoaded}
          docxImageTag="kart1"
          label="Kart 1"
          onUpdateZoomLevel={onUpdateZoomLevel}
          zoomLevel={zoomLevel}
        />
      )}
      {activeMap === "kart2" && (
        <SingleGeoteknikReportGisMap
          pinVisible={pinVisible ? "YES" : "NO"}
          circleVisible={circleVisible ? "YES" : "NO"}
          sirkelDiameter={sirkelDiameter}
          mapId="39bf7f38ae82498b8427a3be4cb3a4e1"
          groupId="43b82823b8d34a438add6c6e88a79b0c"
          pin={gisPoint}
          storeScreenShot={storeScreenShot}
          onLoaded={logicWhenMapIsLoaded}
          docxImageTag="kart2"
          label="Kart 2"
          basemapId="c6958eed33c944149a9e309f28c89ed5"
          onUpdateZoomLevel={onUpdateZoomLevel}
          zoomLevel={zoomLevel}
        />
      )}
      {activeMap === "kart3" && (
        <SingleGeoteknikReportGisMap
          pinVisible={pinVisible ? "YES" : "NO"}
          circleVisible={circleVisible ? "YES" : "NO"}
          sirkelDiameter={sirkelDiameter}
          mapId="39bf7f38ae82498b8427a3be4cb3a4e1"
          groupId="43b82823b8d34a438add6c6e88a79b0c"
          pin={gisPoint}
          storeScreenShot={storeScreenShot}
          onLoaded={logicWhenMapIsLoaded}
          docxImageTag="kart3"
          label="Kart 3"
          layerIds={["wms_7250"]}
          onUpdateZoomLevel={onUpdateZoomLevel}
          zoomLevel={zoomLevel}
        />
      )}
      {activeMap === "kart4" && (
        <SingleGeoteknikReportGisMap
          pinVisible={pinVisible ? "YES" : "NO"}
          circleVisible={circleVisible ? "YES" : "NO"}
          sirkelDiameter={sirkelDiameter}
          mapId="39bf7f38ae82498b8427a3be4cb3a4e1"
          groupId="43b82823b8d34a438add6c6e88a79b0c"
          pin={gisPoint}
          storeScreenShot={storeScreenShot}
          onLoaded={logicWhenMapIsLoaded}
          docxImageTag="kart4"
          label="Kart 4"
          layerIds={["18461deed91-layer-64", "wms_1084", "wms_5457"]}
          onUpdateZoomLevel={onUpdateZoomLevel}
          zoomLevel={zoomLevel}
        />
      )}
    </Stack>
  );
};

export default GeoteknikMaps;

const GisPoint: React.FC<{
  geoteknikKoordinater: IGeoteknikKoordinater;
  onUpdateGeoteknikKoordinater: (koordinater: IGeoteknikKoordinater) => void;
}> = ({ geoteknikKoordinater, onUpdateGeoteknikKoordinater }) => {
  if (!geoteknikKoordinater) return null;

  const updateItem = (key: string, value: any) => {
    onUpdateGeoteknikKoordinater({ ...geoteknikKoordinater, [key]: value });
  };

  const isSoneValid = (sone: string | number) => [32, 33].includes(Number(sone));

  return (
    <Stack gap="xs">
      <Group grow>
        <Text fw={labelWeight}>Sone</Text>
        <NumberInput
          value={Number(geoteknikKoordinater.sone)}
          onChange={(value) => updateItem("sone", value)}
          error={!isSoneValid(geoteknikKoordinater.sone) ? "Sone ikke gyldig" : undefined}
          size="sm"
        />
      </Group>

      <Group grow>
        <Text fw={labelWeight}>Nord</Text>
        <NumberInput
          value={Number(geoteknikKoordinater.nord)}
          onChange={(value) => updateItem("nord", value)}
          size="sm"
        />
      </Group>

      <Group grow>
        <Text fw={labelWeight}>Øst</Text>
        <NumberInput
          value={Number(geoteknikKoordinater.ost)}
          onChange={(value) => updateItem("ost", value)}
          size="sm"
        />
      </Group>
    </Stack>
  );
};

export const convertMaconomyCoordinatesToGeoteknikCoordinates = (
  maconomyCoords: IMaconomyCoordinates,
): IGeoteknikKoordinater => {
  const { UTMN, UTME, UTMZ } = maconomyCoords;

  if (UTMN === null || UTME === null || UTMZ === null) {
    throw new Error("Ogiltiga koordinater. Vänligen ange värden för UTMN, UTME och UTMZ.");
  }

  const sone = UTMZ;
  const nord = UTMN;
  const ost = UTME;

  return { sone, nord, ost };
};

const convertGeoteknikKoordinaterToGisPoints = (koordinater: IGeoteknikKoordinater): IGisPoint => {
  const { sone, nord, ost } = koordinater;

  if (sone === null || nord === null || ost === null || sone === "" || nord === "" || ost === "") {
    return null;
  }

  let x = Number(ost);
  let y = Number(nord);
  const wkid = oversattZonToArcgisWkid(Number(sone));

  if (sone === 32 || sone === "32") {
    try {
      const firstProjection =
        'PROJCS["ETRS89 / UTM zone 32N",GEOGCS["ETRS89",DATUM["European_Terrestrial_Reference_System_1989",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","6258"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.01745329251994328,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4258"]],UNIT["metre",1,AUTHORITY["EPSG","9001"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",9],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],AUTHORITY["EPSG","25832"],AXIS["Easting",EAST],AXIS["Northing",NORTH]]';
      const secondProjection =
        'PROJCS["ETRS89 / UTM zone 33N",GEOGCS["ETRS89",DATUM["European_Terrestrial_Reference_System_1989",SPHEROID["GRS 1980",6378137,298.257222101,AUTHORITY["EPSG","7019"]],AUTHORITY["EPSG","6258"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.01745329251994328,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4258"]],UNIT["metre",1,AUTHORITY["EPSG","9001"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",15],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],AUTHORITY["EPSG","25833"],AXIS["Easting",EAST],AXIS["Northing",NORTH]]';
      const resp = proj4(firstProjection, secondProjection, [x, y]);

      return {
        x: resp[0],
        y: resp[1],
        wkid: 25833,
      };
    } catch (error) {
      console.error(error);
    }
  }

  return { x, y, wkid };
};

const oversattZonToArcgisWkid = (sone: number): number => {
  // Översättningstabell för några vanliga zoner
  const zonOversattningstabell = {
    32: 25832,
    33: 25833,
    // Lägg till fler översättningar här vid behov
  };

  // Kolla om zonen finns i översättningstabellen
  if (zonOversattningstabell.hasOwnProperty(sone)) {
    return zonOversattningstabell[sone];
  } else {
    return null;
  }
};
